import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Check, Warning} from '../../icons';
import {paceNoticeOf} from '../pace';
import {formatMoney} from './BudgetAmount';
import {ScreenTracker} from '../../../utils/ScreenTracker';

/**
 * «Este mes va más lento / más rápido» debajo del monto de la cuenta (C-11 del 15/10, rehecho en
 * UD-22 el 27/10).
 *
 * El paso 4 propone lo que cada cuenta gastó el mes pasado, y si este mes gasta muy distinto la línea
 * nace en rojo en la primera pantalla de la agencia. Se avisa acá, donde el número todavía se puede
 * cambiar, con los DOS montos a la vista: el del mes pasado (marcado, porque es el que va si no se
 * toca nada) y el del ritmo de este mes. Antes era un párrafo con tres ideas y un link «Usar…»: fede
 * no entendió qué decía (26/10, card x1LOhCP5) y, una vez apretado, no había forma de volver.
 *
 * El porqué (de qué color nacería la línea, y cuándo conviene el monto del mes pasado) va detrás de
 * «¿Por qué?»: la proyección no es «la correcta» —si la inversión sube más adelante, el monto bueno es
 * el del mes pasado, y eso lo sabe la agencia, no nosotros (fserb-12, 15/10)—.
 *
 * Con varias cuentas a la vista nombra la cuenta (`showAccountName`); debajo de la subfila de su
 * cuenta, no. Lo que el usuario escribió a mano no se avisa: su número gana (`paceNoticeOf`).
 */
export function PaceNotice({entries, budgets, budgetEdits, dispatch, showAccountName = false}) {
    const notices = (entries || [])
        .map(entry => {
            const suggestion = budgets?.[String(entry.pk)];
            return {entry, suggestion, notice: paceNoticeOf(suggestion, budgetEdits?.[entry.pk])};
        })
        .filter(each => each.notice);
    if (!notices.length) return null;

    return (
        <div className="ob-pace">
            {notices.map(({entry, suggestion, notice}) => (
                <PaceLine key={entry.pk} entry={entry} currency={suggestion.currency} notice={notice}
                          dispatch={dispatch} showAccountName={showAccountName}/>
            ))}
        </div>
    );
}

function PaceLine({entry, currency, notice, dispatch, showAccountName}) {
    const {t, i18n} = useTranslation();
    const [whyOpen, setWhyOpen] = useState(false);
    const money = value => formatMoney(value, currency, i18n.language);
    const values = {account: entry.account?.name || entry.account?.account_id, days: notice.days,
                    spent: money(notice.spent), projected: money(notice.projected), last: money(notice.last),
                    level: t(`onboarding.budgets.pace_level_${notice.level}`),
                    interpolation: {escapeValue: false}};
    const direction = notice.zero ? 'zero' : (notice.slower ? 'slower' : 'faster');

    const keepLast = () => {
        if (notice.chosen === 'last') return;
        dispatch({type: 'CLEAR_BUDGET_EDIT', pk: entry.pk});
    };
    const useProjection = () => {
        if (notice.chosen === 'pace') return;
        dispatch({type: 'EDIT_BUDGET', pk: entry.pk, amount: notice.projected, currency});
    };
    const toggleWhy = () => {
        if (!whyOpen) ScreenTracker.recordEvent('onboarding_pace_why');
        setWhyOpen(!whyOpen);
    };

    return (
        <div className="ob-pace__line" role="group"
             aria-label={t('onboarding.budgets.pace_group_label', values)}>
            <p className="ob-pace__text">
                <Warning size={14} aria-hidden="true"/>
                <span>
                    {showAccountName && <span className="ob-pace__account">{values.account}: </span>}
                    <b>{t(`onboarding.budgets.pace_${direction}`)}</b>
                    {' '}{t(notice.zero ? 'onboarding.budgets.pace_detail_zero' : 'onboarding.budgets.pace_detail',
                            values)}
                </span>
            </p>
            <div className="ob-pace__actions">
                {/* Sin gasto este mes no hay un monto que ofrecer: «ARS 0» sería una línea sin
                    presupuesto, y para eso está «Sin presupuesto» en el editor. */}
                {!notice.zero && (
                    <div className="ob-pace__choice">
                        <PaceOption on={notice.chosen === 'last'} onClick={keepLast}>
                            {t('onboarding.budgets.pace_keep', values)}
                        </PaceOption>
                        <PaceOption on={notice.chosen === 'pace'} onClick={useProjection}>
                            {t('onboarding.budgets.pace_use', values)}
                        </PaceOption>
                    </div>
                )}
                <button type="button" className="ob-pace__why-toggle" aria-expanded={whyOpen} onClick={toggleWhy}>
                    {whyOpen ? t('onboarding.budgets.pace_why_hide') : t('onboarding.budgets.pace_why')}
                </button>
            </div>
            {whyOpen && <p className="ob-pace__why">{t(`onboarding.budgets.pace_why_${direction}`, values)}</p>}
        </div>
    );
}

/** Una de las dos opciones. La elegida va con relleno, tilde y peso: un tinte solo no se lee. */
function PaceOption({on, onClick, children}) {
    return (
        <button type="button" className={`ob-pace__option${on ? ' -on' : ''}`} aria-pressed={on} onClick={onClick}>
            {on && <Check size={13} weight="bold" aria-hidden="true"/>}
            {children}
        </button>
    );
}
